import { FC, lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Toolbar from '@mui/material/Toolbar'
import { Casing } from '@common/helpers/casing'
import { IClientPortalInfo } from '@common/interfaces/client'
import { IAimProfilePatch, IAimStatus, IUser } from '@common/interfaces/user'
import cn from 'classnames'
// The same mark the desktop rail renders when collapsed. `logo-small-client.svg` is the
// client-intake variant — one mark for the app, so the header and the rail can't diverge.
import SmallLogo from '../../assets/images/D-logo.svg?react'
import { ROUTES } from '../../constants/routes'
import { setBaseTitle } from '../../helpers/documentTitle'
import { currentWorkFlow } from '../../helpers/helpers'
import { usePermissions } from '../../helpers/permissionContext'
import { useRouteTitle } from '../../hooks/useRouteTitle'
import { useTouchPress } from '../../hooks/useTouchPress'
import AimStatusMenuItem from '../AimStatusModal/AimStatusMenuItem'
import { useOwnAimStatus } from '../AimStatusModal/useOwnAimStatus'
import AimStatusEmoji from '../Common/AimStatus'
import Avatar from '../Common/Avatar'
import BackTrail from '../Common/BackTrail'
import CopyPageLink from '../Common/CopyPageLink'
import Icon from '../Common/Icon'
import IconButton from '../Common/IconButton'
import Menu from '../Common/Menu'
import { setPageTitleActionsSlot } from '../Common/PageTitleActions'
import Text from '../Common/Text'
import ContextSwitcherItems from '../ContextSwitcherItems'
import { openFeedbackWidget } from '../FeedbackWidget/feedbackWidgetOpenStore'
import GlobalSearch from '../GlobalSearch'
import { titles } from '../HeaderClient/titles'
import NotificationPane from '../NotificationPane'
import styles from './MobileHeader.module.scss'

// Opened from the account menu; it carries the emoji picker, so it loads on first open.
const AimStatusModal = lazy(() => import('../AimStatusModal/AimStatusModal'))

export interface IMobileHeaderProps {
  toggleNotes?: () => void
  user: IUser | null
  pageTitle?: string
  pageTitleCasing?: Casing | 'role'
  onLogout: () => void
  isAdminLoggedAsClient?: boolean
  clientInfo?: IClientPortalInfo | null
  onSwitchClient?: (clientId: string) => void
  ownAimStatus: IAimStatus | null
  updateAimProfile: (patch: IAimProfilePatch) => unknown
}

const MobileHeader: FC<IMobileHeaderProps> = ({
  toggleNotes,
  user,
  pageTitle,
  pageTitleCasing = 'role',
  onLogout,
  isAdminLoggedAsClient,
  clientInfo,
  onSwitchClient,
  ownAimStatus,
  updateAimProfile,
}) => {
  const { pathname } = useLocation()
  const [closeMenuCount, setCloseMenuCount] = useState(0)

  const workflow = useMemo(() => currentWorkFlow(pathname), [pathname])

  // Admin-as-client carries the client role, so this also hides status there, as on desktop.
  const { isClientUser, isMessagingUser } = usePermissions()

  // Check if we should show notes button
  // Show if workflow exists OR if user is CLIENT_USER on homepage/dashboard
  const shouldShowNotesButton = useMemo(() => {
    if (!toggleNotes || isClientUser) return false

    // If workflow is detected, show the button
    if (workflow) return true

    // For client users on homepage/dashboard, show the button even if workflow isn't detected
    const isHomePage = pathname === ROUTES.HOMEPAGE || pathname === '/'

    return isHomePage
  }, [workflow, toggleNotes, pathname, isClientUser])

  const routeTitle = useRouteTitle(titles)

  // The browser tab / PWA title. Previously set by the desktop Header, which used to mount
  // on mobile purely because nothing stopped it; now that it does not, this is the only
  // thing keeping the document title in step with the route on a phone.
  useEffect(() => {
    if (routeTitle) {
      setBaseTitle(routeTitle)
    }
  }, [routeTitle])

  const displayTitle = useMemo(
    () => routeTitle || pageTitle || 'Dwight Funding',
    [routeTitle, pageTitle],
  )

  const userName = useMemo(
    // Nickname first, matching the desktop header.
    () =>
      isAdminLoggedAsClient
        ? clientInfo?.clientName || ''
        : user?.nickname?.trim() || user?.firstName || '',
    [isAdminLoggedAsClient, user, clientInfo],
  )

  const handleUserMenuClose = useCallback(() => {
    setCloseMenuCount((prev) => prev + 1)
  }, [])

  const handleLogout = useCallback(() => {
    handleUserMenuClose()
    onLogout()
  }, [onLogout, handleUserMenuClose])

  const handleNavigateToSettings = useCallback(() => {
    handleUserMenuClose()
  }, [handleUserMenuClose])

  const {
    liveStatus,
    isStatusModalOpen,
    handleOpenStatusModal,
    handleCloseStatusModal,
    handleSaveStatus,
  } = useOwnAimStatus(ownAimStatus, updateAimProfile)

  // Close the menu as the dialog opens, or the menu paper sits over it.
  const handleSetStatus = useCallback(() => {
    handleUserMenuClose()
    handleOpenStatusModal()
  }, [handleUserMenuClose, handleOpenStatusModal])

  // The mobile entry point to the feedback panel (see feedbackWidgetOpenStore).
  const handleSubmitFeedback = useCallback(() => {
    handleUserMenuClose()
    openFeedbackWidget()
  }, [handleUserMenuClose])

  // A client user who belongs to several companies switches between them here.
  const companyOptions = useMemo(
    () =>
      isClientUser && !isAdminLoggedAsClient
        ? (user?.clientMemberships ?? []).map(({ clientId, clientName }) => ({
            value: clientId,
            label: clientName,
          }))
        : [],
    [isAdminLoggedAsClient, isClientUser, user],
  )

  const handleSwitchCompany = useCallback(
    (clientId: string) => {
      handleUserMenuClose()
      onSwitchClient?.(clientId)
    },
    [handleUserMenuClose, onSwitchClient],
  )

  const [isSearchOpen, setIsSearchOpen] = useState(false)

  // The bar's controls answer a finger the moment it lands. MUI's own ripple holds a touch
  // for 80ms before it starts drawing — to be sure the touch is not a scroll — and then
  // eases in over half a second, which on a control that opens a panel instantly is
  // feedback that arrives after the thing it was acknowledging. `disableRipple` below
  // hands each of them to `press-dim` instead, which is the treatment iOS gives a bar
  // button: the glyph dims, nothing is painted around it.
  const setToolbarNode = useTouchPress()

  const handleOpenSearch = useCallback(() => {
    setIsSearchOpen(true)
  }, [])

  const handleCloseSearch = useCallback(() => {
    setIsSearchOpen(false)
  }, [])

  return (
    <Box className={styles.mobileHeaderRoot}>
      <AppBar position="fixed" color="default" elevation={0} className={styles.appBar}>
        <Toolbar ref={setToolbarNode} className={styles.toolbar}>
          {/* Left: Small D Logo */}
          <Box className={styles.leftSection}>
            <Link
              component={RouterLink}
              to={ROUTES.HOMEPAGE}
              data-press
              className={styles.logoContainer}
              underline="none"
            >
              <SmallLogo className={styles.logo} />
            </Link>
          </Box>

          {/* Center: Page Title or Search. The title stays mounted (hidden) while searching so
              the PageTitleActions slot doesn't detach and bounce a page's controls elsewhere. */}
          {isSearchOpen && (
            <Box className={styles.searchContainer}>
              <GlobalSearch isMobile onClose={handleCloseSearch} />
            </Box>
          )}
          <Box className={cn(styles.titleSection, { [styles.titleSectionHidden]: isSearchOpen })}>
            <BackTrail />
            <Text
              variant="sectionTitle"
              tone="primary"
              casing={routeTitle ? 'role' : pageTitleCasing}
              component="span"
              className={styles.pageTitle}
              noWrap
            >
              {displayTitle}
            </Text>
            <div ref={setPageTitleActionsSlot} className={styles.titleActions} />
            <CopyPageLink />
          </Box>

          {/* Right: Search, Notifications, Notes and Profile */}
          <Box className={styles.rightSection}>
            {!isClientUser && !isAdminLoggedAsClient && !isSearchOpen && (
              <IconButton
                title="Open search"
                onClick={handleOpenSearch}
                data-press
                disableRipple
                className={styles.searchButton}
                size="small"
              >
                <Icon name="search" size="md" />
              </IconButton>
            )}
            {!isSearchOpen && !isClientUser && !isAdminLoggedAsClient && <NotificationPane />}
            {!isSearchOpen && shouldShowNotesButton && (
              <IconButton
                title="Toggle notes"
                onClick={toggleNotes}
                data-press
                disableRipple
                className={styles.notesButton}
                size="small"
              >
                <Icon name="activity-bars" size="md" />
              </IconButton>
            )}
            {user && (
              <Menu
                closeMenuCount={closeMenuCount}
                paperClassName={styles.userMenu}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                renderTrigger={(open) => (
                  <IconButton
                    title="User menu"
                    onClick={open}
                    data-press
                    disableRipple
                    className={styles.avatarButton}
                    size="small"
                  >
                    <Avatar user={user} clientInfo={isAdminLoggedAsClient ? clientInfo : null} />
                  </IconButton>
                )}
              >
                <Box className={styles.userMenuHeader}>
                  <Text
                    variant="title"
                    tone="primary"
                    casing="none"
                    component="div"
                    className={styles.userMenuName}
                  >
                    Hello, {userName}
                    {isMessagingUser && <AimStatusEmoji userId={user.id} statusSource={user} />}
                  </Text>
                </Box>
                <ContextSwitcherItems
                  title="Switch company"
                  options={companyOptions}
                  currentValue={clientInfo?.id}
                  itemClassName={styles.menuItem}
                  onSelect={handleSwitchCompany}
                />
                {isMessagingUser && (
                  <AimStatusMenuItem
                    status={liveStatus}
                    className={styles.menuItem}
                    onClick={handleSetStatus}
                  />
                )}
                {!isAdminLoggedAsClient && (
                  <Link
                    className={styles.menuItemLink}
                    component={RouterLink}
                    to={isClientUser ? ROUTES.SETTINGS : ROUTES.SETTINGS_PROFILE}
                    underline="none"
                  >
                    <MenuItem onClick={handleNavigateToSettings} className={styles.menuItem}>
                      Settings
                    </MenuItem>
                  </Link>
                )}
                <MenuItem onClick={handleSubmitFeedback} className={styles.menuItem}>
                  Submit Feedback
                </MenuItem>
                <MenuItem onClick={handleLogout} className={styles.menuItem}>
                  Sign out
                </MenuItem>
              </Menu>
            )}
            {/* Outside the Menu, which unmounts its items as it closes. */}
            {isStatusModalOpen && (
              <Suspense fallback={null}>
                <AimStatusModal
                  open
                  currentStatus={liveStatus}
                  onClose={handleCloseStatusModal}
                  onSave={handleSaveStatus}
                />
              </Suspense>
            )}
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  )
}

export default MobileHeader
